import { assign, setup } from 'xstate';
import { routeMessage } from './actions';
import { createWebSocketActor } from './actors';
import { WebSocketEvents } from './events';
import type { MessageType } from './message-types';
import { WebSocketContext, WebSocketInput } from './types';

export const websocketService = setup({
  types: {
    context: {} as WebSocketContext,
    events: {} as WebSocketEvents,
    input: {} as WebSocketInput,
  },
  actors: {
    websocketConnection: createWebSocketActor,
  },
  actions: {
    setUserId: assign(({ event }) => {
      const e = event as Extract<WebSocketEvents, { type: 'ON_CONNECT' }>;
      return { userId: e.payload?.userId };
    }),
    setConnected: assign(({ event }) => {
      const e = event as Extract<WebSocketEvents, { type: 'ON_CONNECTED' }>;
      return { socket: e.payload.socket };
    }),
    setError: assign(({ event }) => {
      const e = event as Extract<WebSocketEvents, { type: 'ON_ERROR' }>;
      return { error: e.payload.error };
    }),
    routeMessage: assign(({ context, event, self }) => {
      const e = event as Extract<WebSocketEvents, { type: 'ON_MESSAGE_RECEIVED' }>;
      routeMessage({
        context,
        event: e,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        self: self as any,
      });
      return {};
    }),
    setLastPing: assign({
      lastPing: () => new Date(),
    }),
    clearConnection: assign({
      socket: null,
      connectionId: null,
    }),
    clearError: assign({
      error: null,
      reconnectAttempts: 0,
    }),
    incrementReconnectAttempts: assign({
      reconnectAttempts: ({ context }) => context.reconnectAttempts + 1,
    }),
    resetReconnectAttempts: assign({
      reconnectAttempts: () => 0,
    }),
  },
  delays: {
    ON_RECONNECT_DELAY: ({ context }) => context.config.reconnectDelay || 2000,
  },
}).createMachine({
  id: 'websocket',
  initial: '$_ACTIVE',
  context: ({ input }) => ({
    config: input.config,
    socket: null,
    connectionId: null,
    userId: input.userId || undefined,
    error: null,
    reconnectAttempts: 0,
    lastPing: null,
    subscriptions: new Set<MessageType>([
      'chatbot',
      'notification',
      'notification.user',
      'ai-job.user',
      'provider-association.user',
    ]), // Subscribe to all by default
  }),
  states: {
    $_DISCONNECTED: {
      on: {
        ON_CONNECT: {
          target: '$_ACTIVE',
          actions: 'setUserId',
        },
      },
    },
    $_ACTIVE: {
      invoke: {
        src: 'websocketConnection',
        input: ({ context }) => ({
          config: context.config,
          userId: context.userId || undefined,
        }),
      },
      initial: '$_CONNECTING',
      states: {
        $_CONNECTING: {
          on: {
            ON_CONNECTED: {
              target: '$_CONNECTED',
              actions: ['setConnected', 'resetReconnectAttempts'],
            },
            ON_ERROR: {
              target: '#websocket.$_RECONNECTING',
              actions: [
                'setError',
                ({ event }) =>
                  console.warn(
                    '[WEBSOCKET SERVICE] Connection error, will retry:',
                    event.payload.error
                  ),
              ],
            },
          },
        },
        $_CONNECTED: {
          on: {
            ON_MESSAGE_RECEIVED: {
              actions: ['routeMessage'],
            },
            ON_PING_RECEIVED: {
              actions: 'setLastPing',
            },
            ON_DISCONNECTED: {
              target: '#websocket.$_RECONNECTING',
              actions: () =>
                console.log('[WEBSOCKET SERVICE] Disconnected, will attempt to reconnect'),
            },
            ON_ERROR: {
              target: '#websocket.$_RECONNECTING',
              actions: [
                'setError',
                ({ event }) =>
                  console.warn(
                    '[WEBSOCKET SERVICE] Error while connected, will reconnect:',
                    event.payload.error
                  ),
              ],
            },
          },
        },
      },
      on: {
        ON_DISCONNECT: {
          target: '$_DISCONNECTING',
        },
      },
    },
    $_DISCONNECTING: {
      entry: [
        ({ context }) => {
          context.socket?.close();
        },
        'clearConnection',
      ],
      always: '$_DISCONNECTED',
    },
    $_RECONNECTING: {
      after: {
        ON_RECONNECT_DELAY: [
          {
            target: '$_ACTIVE',
            guard: ({ context }: { context: WebSocketContext }) =>
              context.reconnectAttempts < (context.config.maxReconnectAttempts || 5),
            actions: 'incrementReconnectAttempts',
          },
          { target: '$_ERROR' },
        ],
      },
    },
    $_ERROR: {
      entry: ({ context }) => {
        console.error('[WEBSOCKET SERVICE] Entered error state:', context.error);
        console.error(
          '[WEBSOCKET SERVICE] Will attempt auto-reconnection in',
          context.config.reconnectDelay || 2000,
          'ms'
        );
        // Close existing socket if any
        context.socket?.close();

        // Show user-friendly notification about connection issues
        // Only for persistent errors, not transient ones
        if (context.reconnectAttempts === 0) {
          console.warn('[WEBSOCKET SERVICE] First connection error - this may be temporary');
        }
      },
      on: {
        ON_MESSAGE_RECEIVED: {
          actions: () => {
            console.warn('[WEBSOCKET SERVICE] Received message while in error state - ignoring');
          },
        },
        ON_PING_RECEIVED: {
          actions: () => {
            // Ignore pings while in error state
          },
        },
        ON_DISCONNECTED: {
          actions: () => {
            console.warn('[WEBSOCKET SERVICE] Disconnected while in error state');
          },
        },
        ON_RECONNECT: {
          target: '$_ACTIVE',
          actions: [
            'clearError',
            () => console.log('[WEBSOCKET SERVICE] Manual reconnection requested'),
          ],
        },
        ON_CONNECT: {
          target: '$_ACTIVE',
          actions: [
            'clearError',
            () => console.log('[WEBSOCKET SERVICE] Manual connection requested'),
          ],
        },
      },
      // Auto-retry after delay
      after: {
        ON_RECONNECT_DELAY: [
          {
            target: '$_ACTIVE',
            guard: ({ context }: { context: WebSocketContext }) =>
              context.reconnectAttempts < (context.config.maxReconnectAttempts || 5),
            actions: [
              'incrementReconnectAttempts',
              () => console.log('[WEBSOCKET SERVICE] Auto-reconnecting from error state'),
            ],
          },
          {
            target: '$_ERROR',
            actions: () =>
              console.error(
                '[WEBSOCKET SERVICE] Max reconnection attempts reached, staying in error state'
              ),
          },
        ],
      },
    },
  },
});
